import styled, { css } from 'styled-components';

/**
 * The dark surfaces every step of the flow is built from.
 *
 * They started out in step 0's styles. They live here because all three steps now
 * share the look, and a card or heading that drifts on one step is the kind of
 * difference nobody notices until the screens are seen side by side.
 *
 * Nothing here restyles a shared component. The design-system inputs, checkboxes
 * and boxes are light by definition and are used by ReviewOfferWaitlist,
 * ChangePaymentMethod and the v2 app, so the dark treatment is applied by descent
 * through `darkFieldsCss` — scoped to whatever wraps it, never to the component.
 */

export const Card = styled.div`
  width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  padding: 32px 24px 40px;

  @media (min-width: 640px) {
    padding: 32px 40px 40px;
  }
`;

/* Sits outside the card: the step numbering orients you in the flow, it is not
   content of the card. */
export const Eyebrow = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;

  .phase {
    color: #b3abe8;
  }

  .rest {
    color: rgba(255, 255, 255, 0.55);
  }
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 500;
  line-height: 1.2;
  color: #ffffff;
`;

export const Subtitle = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  color: #bebebe;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.3;
  color: #ffffff;
`;

export const SectionSubtitle = styled.p`
  margin: -8px 0 0;
  font-size: 14px;
  color: #bebebe;
`;

export const FieldHint = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #9a9aa8;
`;

export const Divide = styled.hr`
  margin: 32px 0 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

/**
 * Themes the shared light components by descent.
 *
 * The pseudo-classes are explicit because the design system declares
 * :hover/:focus/:disabled at specificity (0,2,0) — a plain descendant selector
 * (0,1,1) would lose.
 */
export const darkFieldsCss = css`
  label {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #e5e5e5;
  }

  input:not([type='checkbox']):not([type='radio']),
  select {
    width: 100%;
    border-radius: 8px;
    border: 1.5px solid rgba(255, 255, 255, 0.14);
    background-color: #2b2a3a;
    /* Deliberately not the padding shorthand: Field offsets padding-left to clear a
       prefix icon, and a shorthand would wipe that out, dropping the text under the
       icon. The design system already supplies the base left padding. */
    padding-top: 14px;
    padding-bottom: 14px;
    padding-right: 16px;
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    line-height: 1.5;
    color: #ffffff;
    outline: none;
  }

  /* The design system paints the select arrow via background-image; overriding
     only background-color preserves the icon. A fixed line-height clips descenders
     in a native select, so normal is used here. */
  select {
    background-position: top 16px right 12px;
    line-height: normal;
    height: 52px;
  }

  select option {
    color: #ffffff;
    background-color: #2b2a3a;
  }

  input:not([type='checkbox']):not([type='radio'])::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }

  input:not([type='checkbox']):not([type='radio']):hover,
  select:hover {
    border-color: rgba(255, 255, 255, 0.28);
  }

  input:not([type='checkbox']):not([type='radio']):focus,
  select:focus {
    border-color: #5968db;
    outline: none;
    box-shadow: 0 0 0 3px rgba(89, 104, 219, 0.4);
  }

  input:not([type='checkbox']):not([type='radio']):disabled,
  select:disabled {
    background-color: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
  }

  /* Browser autofill overrides background and colour; without this the text comes
     out black on the dark field. Mirrors global.ts. */
  input:-webkit-autofill {
    -webkit-text-fill-color: #ffffff;
    -webkit-box-shadow: 0 0 0 1000px #2b2a3a inset;
    caret-color: #ffffff;
  }

  /* FeedbackMessage uses the light theme's statusDanger, which is illegible on
     #13131f. Kept narrow: a looser "last span in a div" selector also matches the
     text inside a Radio, which paints every unselected option red. */
  span[class*='FeedbackMessage'] {
    color: #ff9a9a;
  }
`;

/** The design system's Checkmark is a white span and the text a dark p — both need
 *  inverting on a dark card. */
export const darkCheckboxCss = css`
  p,
  label {
    color: #e5e5e5;
  }

  span[class*='Checkmark'],
  input[type='checkbox'] ~ span {
    background-color: #2b2a3a;
    border-color: rgba(255, 255, 255, 0.24);
  }

  input[type='checkbox']:checked ~ span {
    background-color: #5968db;
    border-color: #5968db;
  }

  input[type='checkbox']:focus-visible ~ span {
    box-shadow: 0 0 0 3px rgba(89, 104, 219, 0.4);
  }
`;

export const DarkFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${darkFieldsCss}

  /* Step 0 has no radios, so it can afford the looser feedback selector for the
     design-system spans that carry no FeedbackMessage class. */
  div > span:last-child:not([class*='Checkmark']) {
    color: #ff9a9a;
  }

  /* The SSN padlock is absolutely positioned by S.LockIcon; without this inset it
     sits on top of the placeholder. */
  .ssn-field input {
    padding-left: 40px;
  }
`;

export const ConsentBox = styled.div<{ $invalid?: boolean }>`
  /* 8px: 12 does not exist on the design system's scale (4 / 8 / 16 / 24). */
  border-radius: 8px;
  border: 1px solid
    ${({ $invalid }) =>
      $invalid ? 'rgba(255, 154, 154, 0.7)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${({ $invalid }) =>
    $invalid ? 'rgba(255, 154, 154, 0.08)' : 'rgba(255, 255, 255, 0.04)'};
  padding: 16px;

  p {
    font-size: 14px;
    line-height: 1.5;
  }

  ${darkCheckboxCss}
`;

/**
 * The one primary CTA of the flow: white on the dark surface.
 *
 * This used to describe itself as "matching the CTA the flow uses on every step" while
 * being imported nowhere — steps 1 and 2 hand-copied a purple version into three separate
 * `!important` blocks, and step 0 went white on its own. The values live here now, once.
 */
const CTA_BG = '#ffffff';
const CTA_BG_HOVER = 'rgba(255, 255, 255, 0.9)';
const CTA_FG = '#362d70';
const CTA_RADIUS = '8px';

export const ctaCss = css`
  border: none;
  border-radius: ${CTA_RADIUS};
  background: ${CTA_BG};
  color: ${CTA_FG};
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  padding: 16px 24px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: ${CTA_BG_HOVER};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * Same CTA, for the steps whose button is the shared `<Button>` rather than a plain one.
 *
 * The variant modifiers inject after these rules and would otherwise win, so every property
 * a variant touches has to be forced — including `background` on its own, since
 * `brandPrimary` paints a gradient, and `color`, which `status="info"`/`"success"` also set.
 * Same constants as above, so the two cannot drift.
 */
export const ctaButtonOverrideCss = css`
  background: ${CTA_BG} !important;
  background-color: ${CTA_BG} !important;
  border: none !important;
  border-radius: ${CTA_RADIUS} !important;
  color: ${CTA_FG} !important;
  font-weight: 600 !important;

  &:hover:not(:disabled) {
    background: ${CTA_BG_HOVER} !important;
    background-color: ${CTA_BG_HOVER} !important;
    color: ${CTA_FG} !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
